import {
  CellTypes,
  type IColumnDefsConfig,
  type ITableData,
} from "@focus/focusUI/lib/components/Tables/FocusTable/Entities";
import type {
  ColumnOrderState,
  VisibilityState,
} from "../../components/table/Table";
import type { CellContext } from "@tanstack/table-core";
import { TIME_SYSTEM } from "../../constants/orbitMetadata";
import { replaceObjectRoleLabels } from "../../utils/objectRoleLabels";

export enum FilterType {
  NUMERICAL = "numerical",
  TEXT = "text",
  DATE = "date",
}

export type HeaderValueType =
  | "UNITLESS"
  | "DISTANCE"
  | "SURFACE"
  | "ANGULAR"
  | "SPEED";

export interface Header {
  /** Tipo semántico del valor (para formato / unidades) */
  "@type": HeaderValueType;

  /** Tipo de filtro soportado por el backend */
  filterType: FilterType;

  /** Campo completo usado por backend y filtros */
  field: string;

  /** Título visible de la columna */
  title: string;

  /** Ancho mínimo de la columna */
  minWidth: number;

  /** Ancho máximo opcional */
  maxWidth?: number;

  /** Formato numérico (d3, e4, etc.) */
  format?: string;

  /** Unidades físicas */
  units?: string;

  /** Indica si la columna es visible por defecto */
  initialView?: boolean;

  /** Renderizador personalizado de la celda */
  customCellRenderer?: (
    rowData: CellContext<ITableData, unknown>,
  ) => React.ReactNode;
}

export type Headers = Header[];
export type ServerColumnConfig = Record<
  string,
  { visible: boolean; order: number; width: number }
>;

export type ColumnLayoutState = {
  visibility: VisibilityState;
  order: ColumnOrderState;
  columns: IColumnDefsConfig[];
};

export const readFormat = (inputFormat) => {
  if (inputFormat) {
    // Admitted formats are:
    // - decimal (e.g. 'd4' : rounds the given value to 4 decimals)
    // - exponential (e.g. 'e6' : converts the given value to exponential notation with 6 decimals)
    const admittedFormattings = /^([dD]|[eE])(\d+)$/g;
    const match = admittedFormattings.exec(inputFormat);

    if (match && match.length === 3) {
      const formatterType = match[1].toLowerCase();
      const formatterValue = match[2];
      switch (formatterType) {
        case "d":
          return (value) => {
            if (typeof value === "number") {
              return value.toFixed(parseInt(formatterValue));
            }
            return value;
          };
        case "e":
          return (value) => {
            if (typeof value === "number") {
              return value.toExponential(parseInt(formatterValue));
            }
            return value;
          };
      }
    }
  }
  return (value) => value?.toString();
};

export function getValueByPath<T = any>(
  obj: Record<string, any>,
  path: string,
  format?: string,
): T | undefined {
  const value = path.split(".").reduce<any>((acc, key) => acc?.[key], obj);
  return readFormat(format)(value);
}

export const betweenDates = (row, columnId, filterValue: string) => {
  const rowDate = new Date(row.getValue(columnId));
  const startDate = new Date(filterValue[0]);
  const endDate = new Date(filterValue[1]);
  return rowDate >= startDate && rowDate <= endDate;
};

export const getColumnFilterOptions = (filterType: FilterType) => {
  switch (filterType) {
    case FilterType.NUMERICAL:
      return CellTypes.NUMBER;
    case FilterType.DATE:
      return CellTypes.DATE;
    case FilterType.TEXT:
    default:
      return CellTypes.STRING;
  }
};

export const getColumnDefinitions = (headers: Headers): IColumnDefsConfig[] => {
  if (!Array.isArray(headers)) {
    return [];
  }

  const colDefs = headers.map((header) => {
    let headerName = replaceObjectRoleLabels(header.title);
    if (header.units) {
      headerName = `${headerName} (${header.units})`;
    }

    if (
      header.filterType === FilterType.DATE &&
      !headerName.toUpperCase().includes(`(${TIME_SYSTEM})`)
    ) {
      headerName = `${headerName} (${TIME_SYSTEM})`;
    }

    const cellType = getColumnFilterOptions(header.filterType);

    let defaultFilterFn;

    const numberEquals = () => {};

    switch (cellType) {
      case CellTypes.NUMBER:
        defaultFilterFn = numberEquals;
        break;
      case CellTypes.DATE:
        defaultFilterFn = "equalsString";
        break;
      default:
        defaultFilterFn = "includesString";
    }

    return {
      headerName,
      key: header.field,
      hide: false,
      minSize: header.minWidth ?? 180,
      customCellRenderer: header.customCellRenderer ?? null,
      customValueGetter: ({ row, key }) =>
        getValueByPath(row, key, header.format),
      cellType,
      filterFn: defaultFilterFn,
    };
  });

  return colDefs;
};

export const actionsColumnName = "actions";

function isServerColumnConfig(value: unknown): value is ServerColumnConfig {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every(
    (v) =>
      typeof v === "object" &&
      v !== null &&
      typeof (v as any).visible === "boolean" &&
      typeof (v as any).order === "number" &&
      typeof (v as any).width === "number",
  );
}

export const buildColumnLayoutState = (
  serverDisplayConfig: ServerColumnConfig,
  defaultVisibleValues: string[],
  completeColumnDefinitions: {
    headerName: string;
    key: string;
    hide: boolean;
    customValueGetter: ({ row, key }: { row: any; key: any }) => any;
    cellType: CellTypes;
  }[],
): ColumnLayoutState => {
  // 1. If there is a layout stored in the server, convert it to VisibilityState
  if (isServerColumnConfig(serverDisplayConfig)) {
    const sortedEntries = Object.entries(serverDisplayConfig).sort(
      ([, a], [, b]) => a.order - b.order,
    );

    const visibility: VisibilityState = {};
    const order: ColumnOrderState = [];

    for (const [key, config] of sortedEntries) {
      visibility[key] = config.visible;
      order.push(key);
    }

    const columns = completeColumnDefinitions.map((col) => {
      const serverCol = serverDisplayConfig[col.key];
      if (!serverCol) return col;

      return {
        ...col,
        size: serverCol.width,
      };
    });

    return { visibility, order, columns };
  }

  if (serverDisplayConfig != null) {
    console.warn("server configuration error. aplicando default values");
  }

  // 2. Build visibility from column definitions
  const visibilityFromDefinitions = completeColumnDefinitions.reduce(
    (acc, element) => {
      acc[element.key] =
        element.key === actionsColumnName ? true : !element.hide;
      return acc;
    },
    {} as VisibilityState,
  );

  // 3. Check if only Actions column is visible
  const visibleColumnsButActionsCol = Object.entries(
    visibilityFromDefinitions,
  ).filter(([colId, isVisible]) => isVisible && colId !== actionsColumnName);

  // 4. If no columns (except Actions) are visible, apply default layout
  const finalVisibility =
    visibleColumnsButActionsCol.length === 0
      ? completeColumnDefinitions.reduce((acc, column) => {
          acc[column.key] =
            defaultVisibleValues.includes(column.key) ||
            column.key === actionsColumnName;
          return acc;
        }, {} as VisibilityState)
      : visibilityFromDefinitions;

  const order: ColumnOrderState = completeColumnDefinitions.map((c) => c.key);

  return {
    visibility: finalVisibility,
    order,
    columns: completeColumnDefinitions,
  };
};

export const buildServerColumnConfig = (
  visibility: VisibilityState,
  order: ColumnOrderState,
  columns: {
    key: string;
    size?: number;
    minSize?: number;
  }[],
): ServerColumnConfig => {
  const orderIndex = new Map(order.map((key, index) => [key, index]));

  return columns.reduce((acc, col) => {
    acc[col.key] = {
      visible: visibility[col.key] ?? true,
      order: orderIndex.get(col.key) ?? Number.MAX_SAFE_INTEGER,
      width: col.size ?? col.minSize ?? 0,
    };
    return acc;
  }, {} as ServerColumnConfig);
};
